import { motion } from "framer-motion";
import axios from "axios";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { emailRegex } from "../../constants/regex";
import { COUNTRIES } from "../../constants/countries";
import { CURRENCIES } from "../../constants/currencies";
import InputText from "./InputText";
import InputSubmit from "./FormSubmit";
import SearchableSelect from "./SearchableSelect";
import { buildEmailHtml } from "./emailTemplate";

// Separate EmailJS account dedicated to the customer registration form.
const EMAILJS_SERVICE_ID = "service_3nqsvmg";
const EMAILJS_USER_ID = "U4YNR1ItHnIFjWXJs";
// Pass-through template in the EmailJS dashboard whose body is exactly:
//   {{{html_content}}}
const EMAILJS_TEMPLATE_ID = "template_mcydf9c";

const animationText = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "tween", ease: "easeInOut", duration: 0.8 },
  },
};

export default function SupplierRegistrationForm() {
  const [exception, setException] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isSendLoading, setSendLoading] = useState(false);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
  }, []);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { country: null, currency: null },
  });

  const onSubmit = async (data) => {
    setSuccess(false);
    setException(false);
    setSendLoading(true);

    const mail = {
      service_id: EMAILJS_SERVICE_ID,
      template_id: EMAILJS_TEMPLATE_ID,
      user_id: EMAILJS_USER_ID,
      template_params: {
        html_content: buildEmailHtml(data),
        // Plain-text fallbacks in case the template ever needs them.
        company_name: data.company_name,
        email: data.email,
      },
    };

    axios
      .post("https://api.emailjs.com/api/v1.0/email/send", mail)
      .then(function () {
        setSuccess(true);
        reset();
      })
      .catch(function (error) {
        console.log(error);
        setException(true);
      })
      .finally(function () {
        setSendLoading(false);
      });
  };

  return (
    <div className="isolate bg-white pt-24 pb-24 px-6 lg:px-8">
      <div className="mx-auto max-w-2xl text-center">
        <motion.h2
          variants={animationText}
          initial="hidden"
          animate="show"
          className="text-3xl md:text-4xl tracking-tight text-gray-900 font-lexend"
        >
          Customer Registration
        </motion.h2>
        <motion.p
          variants={animationText}
          initial="hidden"
          animate="show"
          className="mt-4 text-sm text-grey font-lexend"
        >
          Fill in your details to register as a customer.
        </motion.p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="mx-auto mt-16 max-w-xl lg:max-w-5xl sm:mt-20"
      >
        {/* Section 1 — Company Details */}
        <motion.div
          variants={animationText}
          initial="hidden"
          whileInView="show"
          className="mb-10"
        >
          <h3 className="text-xs font-lexend uppercase tracking-widest text-blue pb-2 border-b border-light-blue mb-6">
            Company Details
          </h3>
          <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <InputText
                id={"company_name"}
                type={"text"}
                errors={errors.company_name}
                input={{ ...register("company_name", { required: true }) }}
                label={""}
                placeholder={"Company Name*"}
              />
            </div>
            <div>
              <InputText
                id={"vat_number"}
                type={"text"}
                errors={errors.vat_number}
                input={{ ...register("vat_number", { required: true }) }}
                label={""}
                placeholder={"VAT Number*"}
              />
            </div>
            <div>
              <InputText
                id={"email"}
                type={"text"}
                errors={errors.email}
                input={{
                  ...register("email", {
                    required: true,
                    pattern: {
                      value: emailRegex,
                      message: "Entered value does not match email format",
                    },
                  }),
                }}
                label={""}
                placeholder={"Email*"}
              />
            </div>
            <div className="sm:col-span-2">
              <InputText
                id={"phone"}
                type={"text"}
                errors={errors.phone}
                input={{ ...register("phone", { required: true }) }}
                label={""}
                placeholder={"Phone*"}
              />
            </div>
          </div>
        </motion.div>

        {/* Section 2 — Sales Manager */}
        <motion.div
          variants={animationText}
          initial="hidden"
          whileInView="show"
          className="mb-10"
        >
          <h3 className="text-xs font-lexend uppercase tracking-widest text-blue pb-2 border-b border-light-blue mb-6">
            Sales Manager
          </h3>
          <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
            <div>
              <InputText
                id={"sales_manager_name"}
                type={"text"}
                errors={errors.sales_manager_name}
                input={{ ...register("sales_manager_name", { required: true }) }}
                label={""}
                placeholder={"Name*"}
              />
            </div>
            <div>
              <InputText
                id={"sales_manager_phone"}
                type={"text"}
                errors={errors.sales_manager_phone}
                input={{ ...register("sales_manager_phone", { required: true }) }}
                label={""}
                placeholder={"Phone*"}
              />
            </div>
          </div>
        </motion.div>

        {/* Section 3 — Registered Office */}
        <motion.div
          variants={animationText}
          initial="hidden"
          whileInView="show"
          className="mb-10"
        >
          <h3 className="text-xs font-lexend uppercase tracking-widest text-blue pb-2 border-b border-light-blue mb-6">
            Registered Office
          </h3>
          <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <InputText
                id={"address"}
                type={"text"}
                errors={errors.address}
                input={{ ...register("address", { required: true }) }}
                label={""}
                placeholder={"Address*"}
              />
            </div>
            <div>
              <InputText
                id={"city"}
                type={"text"}
                errors={errors.city}
                input={{ ...register("city", { required: true }) }}
                label={""}
                placeholder={"City*"}
              />
            </div>
            <div>
              <InputText
                id={"cap"}
                type={"text"}
                errors={errors.cap}
                input={{ ...register("cap", { required: true }) }}
                label={""}
                placeholder={"CAP*"}
              />
            </div>
            <div className="sm:col-span-2">
              <Controller
                name="country"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <SearchableSelect
                    options={COUNTRIES}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Country*"
                    errors={errors.country}
                  />
                )}
              />
            </div>
          </div>
        </motion.div>

        {/* Section 3 — Commercial Terms */}
        <motion.div
          variants={animationText}
          initial="hidden"
          whileInView="show"
          className="mb-10"
        >
          <h3 className="text-xs font-lexend uppercase tracking-widest text-blue pb-2 border-b border-light-blue mb-6">
            Commercial Terms
          </h3>
          <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
            <div>
              <InputText
                id={"payment_terms"}
                type={"text"}
                errors={errors.payment_terms}
                input={{ ...register("payment_terms") }}
                label={""}
                placeholder={"Payment Terms"}
              />
            </div>
            <div>
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <SearchableSelect
                    options={CURRENCIES}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Currency"
                    errors={errors.currency}
                    displayKey="code"
                    searchKeys={["code", "name"]}
                  />
                )}
              />
            </div>
          </div>
        </motion.div>

        {/* Consent */}
        <motion.div
          variants={animationText}
          initial="hidden"
          whileInView="show"
          className="pt-4 relative flex items-start"
        >
          <div className="flex h-6 items-center">
            <input
              id="consent"
              type="checkbox"
              {...register("consent", { required: true })}
              className={`h-4 w-4 border-blue text-blue focus:ring-blue ${
                errors.consent ? "ring-2 ring-red-500" : ""
              }`}
            />
          </div>
          <div className="text-sm font-lexend ml-3 text-grey">
            I consent to the processing of my personal data in accordance with
            the GDPR (Reg. EU 2016/679).*
          </div>
        </motion.div>

        {exception && (
          <div className="mt-5">
            <div className="bg-red-50 border-l-4 border-red-400 p-4">
              <p className="text-sm text-red-700 font-lexend">
                Error sending registration, please try again later.
              </p>
            </div>
          </div>
        )}

        {success && (
          <div className="mt-5 mb-5">
            <div className="bg-green-50 border-l-4 border-green-400 p-4">
              <p className="text-sm text-green-700 font-lexend">
                Registration sent successfully. Our team will contact you as
                soon as possible.
              </p>
            </div>
          </div>
        )}

        <div className="relative my-8 flex justify-end">
          <InputSubmit
            label={"Send Registration"}
            isLoading={isSendLoading}
          />
        </div>
      </form>
    </div>
  );
}
